import React from 'react'
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image'
import cn from 'classnames'
import Container from '../Common/Container'
import { useTranslations } from '../../hooks/use-translations'
import { GlobalContext } from '../../context/GlobalContext'
import Typography, { Size } from '../Common/Typography'

import * as s from './BannerDynamicSection.module.scss'
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react'
import { Pagination, Autoplay } from 'swiper'
import ArrowButton from '../Common/ArrowButton'
import { renderTextWithHighlight } from '../../utils'

interface BannerDynamicProps {
  carouselData: {
    imageDesktop: IGatsbyImageData
    children: JSX.Element
    imageMobile?: IGatsbyImageData
    title?: string
    subTitle?: string
    highlightSubTitleWordsByIdx?: number[]
  }[]
  classNameSubTitle?: string
  classNameHighlightSubTitle?: string
  classNameTitle?: string
  titleSize?: Size
  className?: string
  imgStyle?: React.CSSProperties
  colorShadow?: 'light' | 'dark'
  isUppercase?: boolean
}

const BannerDynamicSection: React.FC<BannerDynamicProps> = ({
  carouselData,
  classNameSubTitle,
  className,
  classNameHighlightSubTitle,
  classNameTitle,
  imgStyle,
  titleSize = 80,
  colorShadow,
  isUppercase = true,
}) => {
  const { t } = useTranslations()
  const { location } = React.useContext(GlobalContext)
  const swiperRef = React.useRef<SwiperRef>(null)
  return (
    <section className={cn(s.section, className)}>
      <Swiper
        ref={swiperRef}
        freeMode={true}
        grabCursor={true}
        navigation={true}
        loop={true}
        pagination={{
          clickable: true,
          horizontalClass: s.pagination_horizontal,
          bulletClass: s.pagination,
          bulletActiveClass: s.active,
        }}
        modules={[Pagination, Autoplay]}
        autoplay={{
          delay: 6000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }}
      >
        {carouselData.map((item, idx) => {
          return (
            <SwiperSlide key={idx}>
              <div className={s.slide}>
                <div
                  className={cn(
                    s.content,
                    colorShadow && s[`shadow_${colorShadow}`],
                    (location?.pathname === '/' ||
                      location?.pathname === '/bim-360-migrate') &&
                      s.content_position_end
                  )}
                >
                  <Container>
                    <div className="flex">
                      {item.title && (
                        <Typography
                          variant="h1"
                          color="white"
                          size={titleSize}
                          isUppercase={isUppercase}
                          className={classNameTitle}
                        >
                          {item.title}
                        </Typography>
                      )}
                    </div>

                    {item.subTitle && (
                      <Typography
                        variant="h2"
                        color="white"
                        className={classNameSubTitle}
                        mt={32}
                      >
                        {renderTextWithHighlight(
                          item.subTitle,
                          item.highlightSubTitleWordsByIdx,
                          classNameHighlightSubTitle
                        )}
                      </Typography>
                    )}
                    {item.children}
                  </Container>
                </div>
                <GatsbyImage
                  image={item.imageDesktop}
                  alt={t('_image_alt')}
                  title={t('_image_title')}
                  style={imgStyle}
                  className={cn(s.image, item.imageMobile && s.hiddenMobile)}
                />
                {item.imageMobile && (
                  <GatsbyImage
                    image={item.imageMobile}
                    alt={t('_image_alt')}
                    title={t('_image_title')}
                    style={imgStyle}
                    className={s.imageMobile}
                  />
                )}
              </div>
            </SwiperSlide>
          )
        })}
        <ArrowButton
          onClick={() => swiperRef.current?.swiper.slidePrev()}
          arrowType="left"
          className={cn(s.btn, s.left)}
        />
        <ArrowButton
          arrowType="right"
          onClick={() => swiperRef.current?.swiper.slideNext()}
          className={cn(s.btn, s.right)}
        />
      </Swiper>
    </section>
  )
}

export default BannerDynamicSection
